import useAlias from '@alias';
import Input from '@components/_elements/Input/Input';
import { Interval, IntervalUnit, SavedSearch } from '@types';
import { ReactElement, useCallback, useMemo } from 'react';
import { POINTS_LIMIT } from './historianUtils';
import { useIntervalValidation } from './useIntervalValidation';

// Enum for interval input types
enum IntervalInputType {
  NUMBER = 'number',
  DROPDOWN = 'dropdown',
}

interface IntervalInputsProps {
  interval: Interval;
  setSearchField: <T>(field: string, value: T) => void;
  activeSearch: SavedSearch;
}

// Configuration for interval input fields
interface IntervalInputConfig {
  value: number | { value: IntervalUnit } | false;
  type: IntervalInputType;
  placeholder: string;
  className: string;
  onChange: (val: string | number) => void;
  options?: IntervalUnit[];
}

export function IntervalInputs({
  interval,
  setSearchField,
  activeSearch,
}: IntervalInputsProps): ReactElement {
  const alias = useAlias();
  const { validOptions } = useIntervalValidation({
    interval,
    activeSearch,
    setSearchField,
    pointsLimit: POINTS_LIMIT,
  });

  const handleValueChange = useCallback(
    (val: string | number): void => {
      const numValue = Number(val);
      if (!isNaN(numValue) && numValue > 0) {
        setSearchField<Interval>('interval', {
          ...interval,
          value: numValue,
        });
      }
    },
    [interval, setSearchField],
  );

  const handleUnitChange = useCallback(
    (val: string | number): void => {
      const index = Number(val);
      if (validOptions[index]) {
        setSearchField<Interval>('interval', {
          ...interval,
          unit: validOptions[index],
        });
      }
    },
    [interval, setSearchField, validOptions],
  );

  const inputConfigs = useMemo(
    (): IntervalInputConfig[] => [
      {
        value: interval.value,
        type: IntervalInputType.NUMBER,
        placeholder: '',
        className: 'dark xs mr',
        onChange: handleValueChange,
      },
      {
        value: interval.unit ? { value: interval.unit } : false,
        type: IntervalInputType.DROPDOWN,
        placeholder: alias('Interval'),
        className: 'width-s',
        onChange: handleUnitChange,
        options: validOptions,
      },
    ],
    [interval, handleValueChange, handleUnitChange, validOptions, alias],
  );

  return (
    <div className='interval-row'>
      {inputConfigs.map((config) => (
        <Input
          key={config.type}
          value={config.value}
          type={config.type}
          placeholder={config.placeholder}
          className={config.className}
          onChange={config.onChange}
          options={config.options || []}
        />
      ))}
    </div>
  );
}
